import type {CloseGesture, SubmitGesture} from '@github-ui/date-picker/types'
import {testIdProps} from '@github-ui/test-id-props'
import type {SelectPanelProps, TouchOrMouseEvent} from '@primer/react'
import {type RefObject, useCallback, useMemo, useRef} from 'react'

import {SystemColumnId} from '../../../api/columns/contracts/memex-column'
import {assertNever} from '../../../helpers/assert-never'
import {FocusType, NavigationDirection} from '../../../navigation/types'
import {CELL_HEIGHT} from '../constants'
import {moveTableFocus, useStableTableNavigation} from '../navigation'

type SummaryCloseGesture =
  | Parameters<SelectPanelProps['onOpenChange']>[1]
  | CloseGesture
  | SubmitGesture
  | 'convert-confirmation'

export type UseSummaryOpenChange = (
  nextOpen: Parameters<SelectPanelProps['onOpenChange']>[0],
  gesture?: SummaryCloseGesture,
  event?: React.KeyboardEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>,
) => void

type UseSummaryAnchorProps = {
  columnId: string | number
  renderButton: () => React.ReactNode
  anchorTestId?: string
  onSummaryClose?: () => void
}

type UseSummaryAnchorReturn = {
  /**
   * A function to render the anchor element for a SelectPanel or AnchoredOverlay.
   * Renders a <summary> element with role="button"
   */
  renderAnchor: (anchorProps: React.HTMLAttributes<HTMLElement>) => React.ReactElement
  /**
   * Internal ref to the anchor element
   */
  anchorRef: RefObject<HTMLElement | null>
  /**
   * Function to maintain focus in the table when clicking outside the overlay. Should be provided to the Overlay's onClickOutside prop.
   */
  onClickOutside: (e: TouchOrMouseEvent) => void
  /**
   * Function to call when open state changes for the SelectPanel or AnchoredOverlay.
   * Handles refocusing the table cell when the panel is closed, or moving focus to the next row on selection with the Enter key.
   */
  onOpenChange: UseSummaryOpenChange
}

export function useSummaryAnchor({
  columnId,
  renderButton,
  anchorTestId,
  onSummaryClose,
}: UseSummaryAnchorProps): UseSummaryAnchorReturn {
  const anchorRef = useRef<HTMLElement | null>(null)
  const {navigationDispatch} = useStableTableNavigation()

  const handleClose = useCallback(() => {
    onSummaryClose?.()
    navigationDispatch(moveTableFocus({focusType: FocusType.Focus}))
  }, [navigationDispatch, onSummaryClose])

  const handleKeyboardSelection = useCallback(() => {
    onSummaryClose?.()
    navigationDispatch(moveTableFocus({focusType: FocusType.Focus, y: NavigationDirection.Next}))
  }, [navigationDispatch, onSummaryClose])

  const renderAnchor = useCallback(
    (anchorProps: React.HTMLAttributes<HTMLElement>) => {
      const wrappedOnClick = (e: React.MouseEvent) => {
        // The default behavior of clicking on an open <summary /> button
        // is to hide the content in its parent <details /> element. We want to prevent
        // this default behavior so that in the scenario where a focused cell is double-clicked,
        // we do not show, and then immediately hide the menu.
        e.stopPropagation()
        e.preventDefault()
        // We want to handle detail being 0 or 1 here to account for keyboard
        // interactions with the summary element, which will fire an `onClick` event
        // when ESC or ENTER is pressed.
        if (e.nativeEvent.detail <= 1) {
          handleClose()
        }
      }

      // Currently there is no way for us to own the anchor ref that is used by the AnchoredOverlay.
      // In the meantime, we use this as a work-around to get a ref, so that we can use it in determining
      // if the anchor was the target of the click in onClickOutside
      anchorRef.current = (
        anchorProps as React.HTMLAttributes<HTMLElement> & {ref: RefObject<HTMLElement | null>}
      ).ref.current

      const anchorTestProps = anchorTestId ? testIdProps(anchorTestId) : {}

      return (
        <summary
          {...anchorProps}
          onClick={wrappedOnClick}
          onKeyDown={anchorProps.onKeyDown}
          role="button"
          tabIndex={anchorProps.tabIndex}
          style={{height: CELL_HEIGHT - 1, outline: 'none', listStyle: 'none'}}
          {...anchorTestProps}
        >
          {renderButton()}
        </summary>
      )
    },
    [renderButton, anchorTestId, handleClose],
  )

  const onOpenChange: UseSummaryOpenChange = useCallback(
    (nextOpen, gesture, event) => {
      if (nextOpen) {
        return
      }

      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      const pressedEnter = event && 'key' in event && event.key === 'Enter'

      switch (gesture) {
        // For close gestures that do not involve a selection, we simply want to return focus to the cell
        case 'escape':
        case 'close':
        case 'cancel':
        case 'click-outside': {
          return handleClose()
        }
        case 'selection':
        case 'submit-click':
        case 'submit-key-press': {
          const submittedWithKeyboard = (pressedEnter && gesture === 'selection') || gesture === 'submit-key-press'

          if (submittedWithKeyboard) {
            return handleKeyboardSelection()
          }

          return handleClose()
        }
        case 'convert-confirmation': {
          if (pressedEnter && columnId === SystemColumnId.Repository) {
            return handleKeyboardSelection()
          }
          return
        }
        case undefined:
        case 'anchor-click':
        case 'anchor-key-press': {
          return
        }
        default: {
          assertNever(gesture)
        }
      }
    },
    [columnId, handleClose, handleKeyboardSelection],
  )

  const onClickOutside = useCallback(
    (e: TouchOrMouseEvent) => {
      if (e.target instanceof Element) {
        const nearestSummary = e.target.closest('summary')
        if (nearestSummary === anchorRef.current) {
          // If the target of the click event is the anchor, then we want to ignore closing the
          // Overlay, as the anchor's onClick will handle this.
          return
        }
      }

      onOpenChange(false, 'click-outside')
    },
    [onOpenChange],
  )

  const props = useMemo(() => {
    return {
      renderAnchor,
      anchorRef,
      onClickOutside,
      onOpenChange,
    }
  }, [renderAnchor, anchorRef, onClickOutside, onOpenChange])

  return props
}
